import React, { createContext, type ReactNode, useContext, useEffect } from 'react'
import preferenceKeys from '../person-page/preference-keys'
import type { ProviderValue } from './PreferenceProvider'
import PreferenceProvider from './PreferenceProvider'

type ColumnLayoutOptions = 'single' | 'double'
type ColumnLayoutValue = ProviderValue<ColumnLayoutOptions>

const ColumnLayoutContext = createContext<ColumnLayoutValue>(['double', () => undefined])

export function useColumnLayout(): ColumnLayoutValue {
  const [columnLayout, setColumnLayout] = useContext(ColumnLayoutContext)

  const setWithLocalStorage = (newValue: ColumnLayoutOptions): void => {
    localStorage.setItem(preferenceKeys.COLUMN_LAYOUT, JSON.stringify(newValue))
    setColumnLayout(newValue)
  }

  return [columnLayout, setWithLocalStorage]
}

function ColumnLayoutLocalStorageSync(): null {
  const [columnLayout] = useContext(ColumnLayoutContext)

  useEffect(() => {
    localStorage.setItem(preferenceKeys.COLUMN_LAYOUT, JSON.stringify(columnLayout))
  }, [columnLayout])

  return null
}

type Props = {
  children: ReactNode
  loadingComponent: ReactNode
}

export default function ColumnLayoutProvider({ children, loadingComponent }: Props): JSX.Element {
  return (
    <PreferenceProvider
      preferenceKey={preferenceKeys.COLUMN_LAYOUT}
      Context={ColumnLayoutContext}
      loadingComponent={loadingComponent}
      defaultValue={'double' as ColumnLayoutOptions}
    >
      <ColumnLayoutLocalStorageSync />
      {children}
    </PreferenceProvider>
  )
}
